import { extractAudio, makeChunks, rms, SILENCE_RMS, SR } from "./audio";
import { loadModel, transcribeChunk } from "./worker";
import { postprocess } from "./postprocess";
import { groupWords, reflowCaptions, SENTENCE } from "./lines";
import { CCError } from "./errors";

const mb = (b) => (b / 1048576).toFixed(0);

// Full local pipeline: extract audio -> load Whisper -> chunked transcription -> clean-up.
export async function transcribeFile({ file, cfg, language, wordList, wordTiming, lines, cache, onState, isCancelled }) {
  const stats = { model: cfg.label, device: cfg.device, wordTiming, wordFallback: false };
  let t = performance.now();
  if (!cache.audio || cache.file !== file) {
    onState({ stage: "extract", pct: 0, detail: "Extracting audio in your browser…" });
    cache.audio = null;
    cache.audio = await extractAudio(file, () =>
      onState({ stage: "extract", pct: 0, detail: "Browser decoder failed — trying FFmpeg.wasm (one-time ~31 MB download)…" })
    );
    cache.file = file;
  }
  stats.extractMs = Math.round(performance.now() - t);
  const audio = cache.audio;
  stats.audioSec = +(audio.length / SR).toFixed(2);
  const level = rms(audio);
  if (audio.length < SR * 0.3 || level < SILENCE_RMS) throw new CCError("NO_SPEECH", `Audio level is near silent (RMS ${level.toFixed(4)}).`);

  t = performance.now();
  onState({ stage: "model", pct: 0, detail: `Preparing ${cfg.label} on ${cfg.modeLabel}…` });
  const files = {};
  await loadModel(cfg, (d) => {
    if (!d.total) return;
    files[d.file] = { l: d.status === "done" ? d.total : d.loaded, t: d.total };
    const vals = Object.values(files);
    const l = vals.reduce((a, f) => a + f.l, 0);
    const tt = vals.reduce((a, f) => a + f.t, 0);
    onState({ stage: "model", pct: tt ? l / tt : 0, detail: `Downloading model: ${mb(l)} / ${mb(tt)} MB (cached after first time)` });
  });
  localStorage.setItem(`cc_model_${cfg.cacheKey}`, "1");
  stats.loadMs = Math.round(performance.now() - t);

  t = performance.now();
  const chunks = makeChunks(audio);
  const raw = [];
  for (let i = 0; i < chunks.length; i++) {
    if (isCancelled()) throw new CCError("CANCELLED", "Transcription stopped.");
    const c = chunks[i];
    onState({ stage: "transcribe", pct: i / chunks.length, detail: `Transcribing chunk ${i + 1} of ${chunks.length}…` });
    if (rms(audio, c.s, c.e) < SILENCE_RMS) continue;
    let r = null;
    if (wordTiming && !stats.wordFallback) {
      try {
        r = await transcribeChunk(audio.slice(c.s, c.e), language, i, true);
        const words = r.chunks
          .filter((ch) => ch.text && ch.text.trim())
          .map((ch) => {
            const [a, b] = ch.timestamp || [];
            const s = c.start + (a ?? 0);
            return { w: ch.text.trim(), s, e: Math.min(c.end, c.start + (b ?? (a ?? 0) + 0.3)) };
          });
        raw.push(...groupWords(words, SENTENCE));
        continue;
      } catch (e) {
        if (e.code === "CANCELLED" || e.code === "OOM") throw e;
        stats.wordFallback = true;
        stats.wordError = e.message;
      }
    }
    r = await transcribeChunk(audio.slice(c.s, c.e), language, i, false);
    for (const ch of r.chunks) {
      const [a, b] = ch.timestamp || [];
      if (ch.text && ch.text.trim()) raw.push({ start: c.start + (a ?? 0), end: Math.min(c.end, c.start + (b ?? c.end - c.start)), text: ch.text });
    }
  }
  stats.transcribeMs = Math.round(performance.now() - t);
  stats.rtf = +(stats.transcribeMs / 1000 / Math.max(0.1, stats.audioSec)).toFixed(2);
  let segs = postprocess(raw, audio, wordList);
  if (!segs.length) throw new CCError("NO_SPEECH", "The model did not recognise any speech in this audio.");
  if (lines && lines !== "off") segs = reflowCaptions(segs, lines);
  stats.captions = segs.length;
  return { segs, stats };
}
